import { OrderInvoicingStatus, OrganizationPaymentType } from 'data/__generated__';

// Moyens de paiement que Totem n'encaisse PAS : la facture Odoo part, et c'est le client qui règle (virement
// le plus souvent) ou personne (`NoPayment`). Le cron `reconcile-odoo-payments` constate le paiement sur le dû
// Odoo et passe la commande à `Paid` — il n'y a jamais de prélèvement à relancer depuis la totadmin.
const UNMANAGED_PAYMENT_TYPES: OrganizationPaymentType[] = [
    OrganizationPaymentType.Odoo,
    OrganizationPaymentType.NoPayment,
];

// Statuts où la facture reste entièrement à produire : rien n'est parti chez Odoo ni chez Stripe.
const AWAITING_INVOICING_STATUSES: OrderInvoicingStatus[] = [
    OrderInvoicingStatus.Pending,
    OrderInvoicingStatus.ToBeInvoiced,
];

// Libellé brut d'un statut du registre, sans le contexte (moyen de paiement, livraison) dont dépend
// `getInvoicingLabel`. Pour une colonne de tableau, où il n'y a ni place ni action : on nomme l'état, point.
export const ORDER_INVOICING_STATUS_LABELS: Record<OrderInvoicingStatus, string> = {
    [OrderInvoicingStatus.Pending]: 'Non facturable',
    [OrderInvoicingStatus.ToBeInvoiced]: 'À facturer',
    [OrderInvoicingStatus.Invoiced]: 'Facturée',
    [OrderInvoicingStatus.Paid]: 'Payée',
    [OrderInvoicingStatus.Excluded]: 'Hors périmètre',
};

export function isPaymentManagedByTotem(paymentType: OrganizationPaymentType): boolean {
    return !UNMANAGED_PAYMENT_TYPES.includes(paymentType);
}

/**
 * Libellé de l'état de facturation, ou `null` quand il n'y a rien à en dire. Chaque statut du registre a le
 * SIEN : le fourre-tout précédent affichait « En attente de facturation » sur `Excluded`, c'est-à-dire sur des
 * commandes qui ne seront jamais facturées (org sans paiement, Freefood inclus au forfait) — et sans bouton
 * pour agir, l'ops n'avait aucune sortie.
 *
 * Tant que la commande n'est pas livrée, on ne dit rien du tout : il n'y a rien à facturer avant la livraison,
 * et l'« attente » en rouge se lisait comme une anomalie à traiter alors qu'aucune action n'est possible (le
 * bouton est masqué lui aussi, cf. `canChargeOrder`). Un statut déjà avancé reste affiché, livraison ou pas :
 * une facture partie est une information, pas du bruit.
 */
export function getInvoicingLabel({
    status,
    paymentType,
    hasBeenDelivered,
}: {
    status: OrderInvoicingStatus;
    paymentType: OrganizationPaymentType;
    hasBeenDelivered: boolean;
}): { label: string; value: string; isValid: boolean } | null {
    if (!hasBeenDelivered && AWAITING_INVOICING_STATUSES.includes(status)) {
        return null;
    }

    switch (status) {
        case OrderInvoicingStatus.Paid:
            return { label: 'Paiement', value: 'Payé', isValid: true };
        case OrderInvoicingStatus.Invoiced:
            return {
                label: 'Facturation',
                value: isPaymentManagedByTotem(paymentType)
                    ? 'Facturée, règlement en cours via Stripe'
                    : 'Facturée, en attente du règlement client',
                isValid: true,
            };
        case OrderInvoicingStatus.Excluded:
            return { label: 'Facturation', value: 'Hors périmètre de facturation', isValid: false };
        default:
            return { label: 'Facturation', value: 'En attente de facturation', isValid: false };
    }
}

/**
 * Le bouton « Facturer la commande » ne s'affiche que sur une commande LIVRÉE et encore facturable. Dès
 * `Invoiced`, l'API refuse (garde anti double facturation) : le laisser visible produisait un clic sans effet
 * suivi d'un toast de succès, et sur une org facturée au jour il postait une 2e facture Odoo.
 */
export function canChargeOrder({
    status,
    hasBeenDelivered,
}: {
    status: OrderInvoicingStatus;
    hasBeenDelivered: boolean;
}): boolean {
    return hasBeenDelivered && AWAITING_INVOICING_STATUSES.includes(status);
}
